import { useCallback, useRef, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
	useDocumentEventListener,
	useWindowEventListener,
} from '@common/hooks/useEventListener';
import { GameType } from '@modules/games/Game/constants/availableGames';
import { game_config } from '@legacyApp/client/game_config';
import { config } from '@legacyApp/client/config';

import { GameActivityDetectorProps } from '@modules/hotjar/types';
import {
	BET_TIME_WINDOW,
	HOTJAR_SCRIPT_TEST_IDS,
	UNIFIED_RESTART_INTERVAL,
	CONSECUTIVE_BET_THRESHOLD,
	BETTING_INACTIVITY_THRESHOLD,
} from '@modules/hotjar/constants';

export const useGameActivityDetector = ({
	isRecording,
	stopRecording,
	startRecording,
}: GameActivityDetectorProps): void => {
	const router = useRouter();
	const unifiedRestartIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Local bet tracking state
	const [manualBetCount, setManualBetCount] = useState(0);
	const [lastBetTime, setLastBetTime] = useState(0);

	const trackManualBet = useCallback((): void => {
		const now = Date.now();

		// Clear any existing inactivity timeout
		if (inactivityTimeoutRef.current) {
			clearTimeout(inactivityTimeoutRef.current);
		}

		const timeSinceLastBet = now - lastBetTime;
		const newBetCount =
			timeSinceLastBet < BET_TIME_WINDOW ? manualBetCount + 1 : 1;

		setManualBetCount(newBetCount);
		setLastBetTime(now);

		if (newBetCount >= CONSECUTIVE_BET_THRESHOLD && isRecording) {
			setTimeout(() => stopRecording('continuous-manual-betting'), 100);

			// Set up inactivity re-enabling timer only when we stop recording
			inactivityTimeoutRef.current = setTimeout(() => {
				startRecording('betting-inactivity');
			}, BETTING_INACTIVITY_THRESHOLD);
		}
	}, [lastBetTime, manualBetCount, isRecording, stopRecording, startRecording]);

	// Create a restart check function that doesn't rely on stale closures
	const checkShouldRestartRecordingRef = useRef<() => void>();

	// Update the function reference whenever dependencies change
	checkShouldRestartRecordingRef.current = () => {
		// Only restart if currently not recording
		if (!isRecording) {
			// Check if automated betting modes ended
			const autobetActive = document.querySelector(
				`[data-testid="${HOTJAR_SCRIPT_TEST_IDS.AUTOBET_ACTIVE}"]`,
			);
			const flashbetActive = document.querySelector(
				`[data-testid="${HOTJAR_SCRIPT_TEST_IDS.FLASHBET_ACTIVE}"]`,
			);

			if (!autobetActive && !flashbetActive) {
				startRecording('unified-restart-check');

				// Clear the interval since we're restarting
				if (unifiedRestartIntervalRef.current) {
					clearInterval(unifiedRestartIntervalRef.current);
					unifiedRestartIntervalRef.current = null;
				}
			}
		}
	};

	// Wrapper function that calls the current implementation
	const checkShouldRestartRecording = useCallback(() => {
		if (checkShouldRestartRecordingRef.current) {
			checkShouldRestartRecordingRef.current();
		}
	}, []);

	const handleBetClick = useCallback(
		(event: Event) => {
			const target = event.target as HTMLElement;

			// Check target and up to 5 parent elements for test ID
			// This handles cases where user clicks on icon/text inside button
			let element: HTMLElement | null = target;
			let testId: string | null = null;
			let depth = 0;

			while (element && depth < 5) {
				testId = element.getAttribute('data-testid');
				if (testId) {
					break;
				}
				element = element.parentElement;
				depth++;
			}

			// Only stop recording for AUTOBET and FLASHBET buttons (automated modes)
			// Manual bet button is handled by handleManualBet which tracks consecutive bets
			if (
				testId &&
				[
					HOTJAR_SCRIPT_TEST_IDS.AUTOBET_BUTTON,
					HOTJAR_SCRIPT_TEST_IDS.FLASHBET_BUTTON,
				].includes(testId as any)
			) {
				stopRecording('automated-betting-started');

				// Wait 6 seconds before starting restart checks
				// (flashbet-active test ID appears after 5 seconds, autobet-active should be immediate)
				// This ensures the DOM elements are present when we start checking
				setTimeout(() => {
					if (!unifiedRestartIntervalRef.current) {
						unifiedRestartIntervalRef.current = setInterval(() => {
							checkShouldRestartRecording();
						}, UNIFIED_RESTART_INTERVAL);
					}
				}, 6000);
			}
		},
		[stopRecording, checkShouldRestartRecording],
	);

	const handleManualBet = useCallback(
		(event: Event): void => {
			if (!isRecording) {
				return;
			}

			const target = event.target as HTMLElement;

			// Check target and up to 5 parent elements for test ID
			let element: HTMLElement | null = target;
			let testId: string | null = null;
			let depth = 0;

			while (element && depth < 5) {
				testId = element.getAttribute('data-testid');
				if (testId) {
					break;
				}
				element = element.parentElement;
				depth++;
			}

			// Only track manual bet button clicks (not autobet or flashbet)
			if (testId === HOTJAR_SCRIPT_TEST_IDS.BET_BUTTON) {
				trackManualBet();

				if (window.hj) {
					window.hj('event', 'manual-bet-placed', { testId });
				}
			}
		},
		[isRecording, trackManualBet],
	);

	const handleHotkeyBetting = useCallback(
		(event: KeyboardEvent): void => {
			if (!isRecording) {
				return;
			}

			// Get game-specific hotkey mappings from actual game configurations
			const getGameHotkeyCodes = (gameType: GameType): number[] => {
				const gameHotkeys = game_config[gameType]?.hotkeys;
				return gameHotkeys?.map((hotkey) => hotkey.keyCode) || [];
			};

			// Get global hotkey codes from actual config
			const getGlobalHotkeyCodes = (): number[] => {
				return config.defaultHotKeys?.map((hotkey) => hotkey.keyCode) || [];
			};

			const GLOBAL_HOTKEYS = getGlobalHotkeyCodes();

			// Check if we're in a game interface (not in input fields)
			const target = event.target as HTMLElement;
			const isInputField =
				['INPUT', 'TEXTAREA'].includes(target.tagName) ||
				target.contentEditable === 'true';

			if (isInputField) {
				return;
			}

			// Detect current game from URL path
			const currentPath = window.location.pathname;
			let currentGame: GameType | null = null;

			// Match game type from URL path (/casino/dice, /casino/hilo, etc.)
			for (const gameType of Object.values(GameType)) {
				if (
					currentPath.includes(`/casino/${gameType}`) ||
					currentPath.includes(`/${gameType}`)
				) {
					currentGame = gameType;
					break;
				}
			}

			// Check if the pressed key matches current game's hotkeys or global hotkeys
			const gameHotkeyCodes = currentGame
				? getGameHotkeyCodes(currentGame)
				: [];
			const isGameHotkey =
				currentGame && gameHotkeyCodes.includes(event.keyCode);
			const isGlobalHotkey = GLOBAL_HOTKEYS.includes(event.keyCode);

			if (isGameHotkey || isGlobalHotkey) {
				trackManualBet();

				if (window.hj) {
					window.hj('event', 'hotkey-bet-placed', {
						game: currentGame,
						keyCode: event.keyCode,
						key: event.code,
						type: isGameHotkey ? 'game-specific' : 'global',
					});
				}
			}
		},
		[isRecording, trackManualBet],
	);

	const handleIframeFocus = useCallback((): void => {
		if (!isRecording) {
			return;
		}

		// Check if the active element is the slots iframe
		const activeElement = document.activeElement;
		if (
			activeElement &&
			activeElement.getAttribute('data-testid') ===
				HOTJAR_SCRIPT_TEST_IDS.SLOTS_IFRAME
		) {
			stopRecording('slots-iframe-interaction');

			// Start unified restart interval if not already running
			if (!unifiedRestartIntervalRef.current) {
				unifiedRestartIntervalRef.current = setInterval(() => {
					checkShouldRestartRecording();
				}, UNIFIED_RESTART_INTERVAL);
			}
		}
	}, [isRecording, stopRecording, checkShouldRestartRecording]);

	// Clear restart interval when recording starts
	useEffect(() => {
		if (isRecording && unifiedRestartIntervalRef.current) {
			clearInterval(unifiedRestartIntervalRef.current);
			unifiedRestartIntervalRef.current = null;
		}
	}, [isRecording]);

	// Handle navigation - check if recording should restart when user navigates
	useEffect(() => {
		if (!isRecording) {
			if (!unifiedRestartIntervalRef.current) {
				unifiedRestartIntervalRef.current = setInterval(() => {
					checkShouldRestartRecording();
				}, UNIFIED_RESTART_INTERVAL);
			}
		}
	}, [router.pathname, isRecording, checkShouldRestartRecording]);

	// Set up event listeners for all game-related interactions
	useDocumentEventListener('click', handleBetClick);
	useDocumentEventListener('click', handleManualBet);
	useWindowEventListener('keydown', handleHotkeyBetting);

	// Detect when user clicks into slots iframe by listening for blur event
	// When window loses focus to iframe, check if active element is slots iframe
	useWindowEventListener('blur', handleIframeFocus);

	// Cleanup function for timeouts and intervals
	useEffect((): (() => void) => {
		return (): void => {
			if (unifiedRestartIntervalRef.current) {
				clearInterval(unifiedRestartIntervalRef.current);
			}
			if (inactivityTimeoutRef.current) {
				clearTimeout(inactivityTimeoutRef.current);
			}
		};
	}, []);
};
